import React from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';

interface IndividualLoadingStates {
  emv: boolean;
  closeLoop: boolean;
  offlinePaymentTypeGroup: boolean;
  tokenWallet: boolean;
  mcc: boolean;
  hashedHotCard: boolean;
  card: boolean;
  cardPrompt: boolean;
  processor: boolean;
  receipt: boolean;
  logo: boolean;
  customGroups: boolean;
  terminalApp: boolean;
}

interface DataLoadingIndicatorProps {
  individualLoading: IndividualLoadingStates;
  isComplete: boolean;
  isLoading: boolean;
}

const DataLoadingIndicator: React.FC<DataLoadingIndicatorProps> = ({
  individualLoading,
  isComplete,
  isLoading
}) => {
  const dataSources = [
    { key: 'emv', label: 'EMV Options' },
    { key: 'closeLoop', label: 'Close Loop Options' },
    { key: 'offlinePaymentTypeGroup', label: 'Offline Payment Type Group' },
    { key: 'tokenWallet', label: 'Token Wallet Options' },
    { key: 'mcc', label: 'MCC Options' },
    { key: 'hashedHotCard', label: 'Hashed Hot Card Options' },
    { key: 'card', label: 'Card Data' },
    { key: 'cardPrompt', label: 'Card Prompt Data' },
    { key: 'processor', label: 'Processor Data' },
    { key: 'receipt', label: 'Receipt Data' },
    { key: 'logo', label: 'Logo Data' },
    { key: 'customGroups', label: 'Custom Groups Data' },
    { key: 'terminalApp', label: 'Terminal App Data' }
  ];

  const getStatusIcon = (isLoading: boolean) => {
    if (isLoading) {
      return <CircularProgress size={16} />;
    }
    return <CheckCircle color="success" />;
  };

  const getStatusColor = (isLoading: boolean) => {
    if (isLoading) {
      return 'warning';
    }
    return 'success';
  };

  if (isComplete) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CheckCircle color="success" />
        <Typography variant="body2" color="success.main">
          All data loaded successfully
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <CircularProgress size={20} />
       </Box>
      
      <Box display="flex" flexWrap="wrap" gap={1}>
        {dataSources.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            icon={getStatusIcon(individualLoading[key as keyof IndividualLoadingStates])}
            color={getStatusColor(individualLoading[key as keyof IndividualLoadingStates])}
            variant="outlined"
            size="small"
          />
        ))}
      </Box>
      
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {Object.values(individualLoading).filter(Boolean).length} of {dataSources.length} loading...
        </Typography>
      </Box>
    </Box>
  );
};

export default DataLoadingIndicator;
